// sim/sensing.ts — bounded, allocation-free neighbour queries for the colony layer.
//
// Both helpers scan a Chebyshev box of radius r around (ax,ay), reading each
// tile's intrusive bucket directly (never an O(n^2) all-pairs scan). Cost is
// O(r^2 * occupancy), independent of total entity count (EFFICIENCY.md §B.4).
// Determinism: tiles are visited in fixed (y,x) order and ties break on the
// lower entity id, so the result is stable for a given world state.

import { KIND, NIL } from '../types.ts';
import type { EntityId, ColonyId } from '../types.ts';
import type { EntitySystem } from '../engine/EntitySystem.ts';
import type { WorldGrid } from '../engine/WorldGrid.ts';
import { chebyshev } from '../engine/Pathing.ts';

/** Nearest on-grid food node (amount > 0) within radius r, or NIL. */
export function senseNearestFood(
  entities: EntitySystem,
  grid: WorldGrid,
  ax: number,
  ay: number,
  r: number,
): EntityId {
  let bestId: EntityId = NIL;
  let bestDist = r + 1;
  let bestTieId = 0x7fffffff;

  const minX = ax - r < 0 ? 0 : ax - r;
  const minY = ay - r < 0 ? 0 : ay - r;
  const maxX = ax + r >= grid.width ? grid.width - 1 : ax + r;
  const maxY = ay + r >= grid.height ? grid.height - 1 : ay + r;

  for (let ty = minY; ty <= maxY; ty++) {
    for (let tx = minX; tx <= maxX; tx++) {
      const ti = grid.index(tx, ty);
      for (let cur = grid.headAt(ti); cur !== NIL; cur = grid.next(cur)) {
        const c = cur as number;
        if ((entities.kind[c] as number) !== KIND.Food) continue;
        if ((entities.foodAmount[c] as number) <= 0) continue;
        const dist = chebyshev(ax, ay, tx, ty);
        if (dist < bestDist || (dist === bestDist && c < bestTieId)) {
          bestDist = dist;
          bestId = cur;
          bestTieId = c;
        }
      }
    }
  }
  return bestId;
}

/**
 * Nearest live organism belonging to a DIFFERENT colony (hp > 0) within radius
 * r, or NIL. The "enemy" query that drives Soldier targeting and the Specialist
 * threat trigger.
 */
export function senseNearestEnemy(
  entities: EntitySystem,
  grid: WorldGrid,
  ax: number,
  ay: number,
  r: number,
  myColony: ColonyId,
): EntityId {
  let bestId: EntityId = NIL;
  let bestDist = r + 1;
  let bestTieId = 0x7fffffff;

  const minX = ax - r < 0 ? 0 : ax - r;
  const minY = ay - r < 0 ? 0 : ay - r;
  const maxX = ax + r >= grid.width ? grid.width - 1 : ax + r;
  const maxY = ay + r >= grid.height ? grid.height - 1 : ay + r;

  for (let ty = minY; ty <= maxY; ty++) {
    for (let tx = minX; tx <= maxX; tx++) {
      const ti = grid.index(tx, ty);
      for (let cur = grid.headAt(ti); cur !== NIL; cur = grid.next(cur)) {
        const c = cur as number;
        if ((entities.kind[c] as number) !== KIND.Organism) continue;
        if ((entities.colony[c] as number) === myColony) continue;
        if ((entities.hp[c] as number) <= 0) continue;
        const dist = chebyshev(ax, ay, tx, ty);
        if (dist < bestDist || (dist === bestDist && c < bestTieId)) {
          bestDist = dist;
          bestId = cur;
          bestTieId = c;
        }
      }
    }
  }
  return bestId;
}

/**
 * Nearest live wildlife (KIND.Wildlife, hp > 0) within radius r, or NIL. The
 * "threat" query that lets a colony Soldier DEFEND against non-colony predators —
 * the two-way half of the ecosystem interaction (predators hunt insects; soldiers
 * cull predators). Returns NIL when no wildlife exists, so colony-only worlds are
 * unaffected (the Soldier falls through to its normal patrol — byte-identical).
 */
export function senseNearestWildlife(
  entities: EntitySystem,
  grid: WorldGrid,
  ax: number,
  ay: number,
  r: number,
): EntityId {
  let bestId: EntityId = NIL;
  let bestDist = r + 1;
  let bestTieId = 0x7fffffff;

  const minX = ax - r < 0 ? 0 : ax - r;
  const minY = ay - r < 0 ? 0 : ay - r;
  const maxX = ax + r >= grid.width ? grid.width - 1 : ax + r;
  const maxY = ay + r >= grid.height ? grid.height - 1 : ay + r;

  for (let ty = minY; ty <= maxY; ty++) {
    for (let tx = minX; tx <= maxX; tx++) {
      const ti = grid.index(tx, ty);
      for (let cur = grid.headAt(ti); cur !== NIL; cur = grid.next(cur)) {
        const c = cur as number;
        if ((entities.kind[c] as number) !== KIND.Wildlife) continue;
        if ((entities.hp[c] as number) <= 0) continue;
        const dist = chebyshev(ax, ay, tx, ty);
        if (dist < bestDist || (dist === bestDist && c < bestTieId)) {
          bestDist = dist;
          bestId = cur;
          bestTieId = c;
        }
      }
    }
  }
  return bestId;
}
